import {
  AcceptInvitationScreen,
  ForgotPasswordScreen,
  loadSession,
  LoginScreen,
  loginSearch,
  MfaEnrollScreen,
  PendingScreen,
  requireMfa,
  requireSession,
  type RouterContext,
  SessionErrorScreen,
  SessionWatcher,
} from '@classgo/app-kit';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
} from '@tanstack/react-router';

import { Playground } from '../playground';
import { AccountScreen } from '../screens/account';
import { ActAsScreen } from '../screens/act-as';
import { AlbertScreen } from '../screens/albert';
import { AnnouncementsScreen } from '../screens/announcements';
import { AttendanceScreen } from '../screens/attendance';
import { BillingScreen } from '../screens/billing';
import { FamilyStatementScreen } from '../screens/billing/statement';
import { ClassroomsScreen } from '../screens/classrooms';
import { ConsentTextsScreen } from '../screens/consents';
import { CurriculumScreen } from '../screens/curriculum';
import { GradesViewScreen } from '../screens/grades-view';
import { GroupsScreen } from '../screens/groups';
import { GuardiansScreen } from '../screens/guardians';
import { HomeScreen } from '../screens/home';
import { ImportsScreen } from '../screens/imports';
import { LevelsScreen } from '../screens/levels';
import { MentoriaScreen } from '../screens/mentoria';
import { ReportCardScreen } from '../screens/report-card';
import { ReportCardsScreen } from '../screens/report-cards';
import { RolesScreen } from '../screens/roles';
import { SedesScreen } from '../screens/sedes';
import { SetupScreen } from '../screens/setup';
import { StudentDetailScreen } from '../screens/student-detail';
import { StudentsScreen } from '../screens/students';
import { SubjectsScreen } from '../screens/subjects';
import { TeacherDetailScreen } from '../screens/teacher-detail';
import { TeachersScreen } from '../screens/teachers';
import { TimetableScreen } from '../screens/timetable';
import { UsersScreen } from '../screens/users';
import { YearDetailScreen } from '../screens/year-detail';
import { YearsScreen } from '../screens/years';
import { AppLayout } from '../shell';
import { guarded } from './guard';

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <>
      <SessionWatcher />
      <Outlet />
    </>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: loginSearch,
  component: LoginScreen,
});

const mfaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mfa',
  validateSearch: loginSearch,
  beforeLoad: ({ context, location }) => requireSession(context, location.href),
  component: MfaEnrollScreen,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordScreen,
});

const AcceptInvitationRoute = () => {
  const { token } = invitationRoute.useParams();
  return <AcceptInvitationScreen token={token} />;
};

const invitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invitacion/$token',
  component: AcceptInvitationRoute,
});

const playgroundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/playground',
  component: Playground,
});

const actAsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/act-as',
  component: ActAsScreen,
});

/**
 * Everything under the shell: the person must be signed in (else login, then back here) and
 * `GET /me` is loaded before the first screen renders.
 */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: ({ context, location }) => requireSession(context, location.href),
  loader: async ({ context, location }) => {
    const session = await loadSession(context, location.href);
    requireMfa(session, location.href);
    return session;
  },
  pendingComponent: PendingScreen,
  errorComponent: SessionErrorScreen,
  component: AppLayout,
});

const homeRoute = createRoute({ getParentRoute: () => appRoute, path: '/', component: HomeScreen });

const accountRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/cuenta',
  component: AccountScreen,
});

const consentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/consentimientos',
  component: guarded(ConsentTextsScreen, 'consents:read'),
});
const sedesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/sedes',
  validateSearch: (search: Record<string, unknown>): { nueva?: boolean } =>
    search['nueva'] === true || search['nueva'] === 'true' ? { nueva: true } : {},
  component: guarded(SedesScreen, 'sedes:read'),
});
const yearsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/anos',
  component: guarded(YearsScreen, 'academic:read'),
});
const yearRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/anos/$yearId',
  component: guarded(YearDetailScreen, 'academic:read'),
});
const levelsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/niveles',
  component: guarded(LevelsScreen, 'academic:read'),
});
const groupsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/grupos',
  component: guarded(GroupsScreen, 'academic:read'),
});
const subjectsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/asignaturas',
  component: guarded(SubjectsScreen, 'subjects:read'),
});
const classroomsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/aulas',
  component: guarded(ClassroomsScreen, 'classrooms:read'),
});
const studentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/estudiantes',
  component: guarded(StudentsScreen, 'students:read'),
});
const studentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/estudiantes/$studentId',
  component: guarded(StudentDetailScreen, 'students:read'),
});
const reportCardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/estudiantes/$studentId/boletin',
  component: guarded(ReportCardScreen, 'grades:read'),
});
const teachersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/docentes',
  component: guarded(TeachersScreen, 'teachers:read'),
});
const teacherRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/docentes/$teacherId',
  component: guarded(TeacherDetailScreen, 'teachers:read'),
});
const guardiansRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/acudientes',
  component: guarded(GuardiansScreen, 'guardians:read'),
});
const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/usuarios',
  component: guarded(UsersScreen, 'users:read'),
});
const rolesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/roles',
  component: guarded(RolesScreen, 'roles:read'),
});
const timetableRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/horario',
  component: guarded(TimetableScreen, 'timetable:read'),
});
const attendanceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/asistencia',
  component: guarded(AttendanceScreen, 'attendance:read'),
});
const gradesViewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/calificaciones',
  component: guarded(GradesViewScreen, 'grades:read'),
});
const reportCardsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/boletines',
  component: guarded(ReportCardsScreen, 'grades:read'),
});
const announcementsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/comunicados',
  component: guarded(AnnouncementsScreen, 'communication:read'),
});
const mentoriaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/mentoria',
  component: guarded(MentoriaScreen, 'mentoria:use'),
});
const albertRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/albert',
  component: guarded(AlbertScreen, 'albert:use', ['TEACHER']),
});
const curriculumRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/plan-de-estudios',
  component: guarded(CurriculumScreen, 'curriculum:read'),
});
const billingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/cobros',
  component: guarded(BillingScreen, 'billing:read'),
});
const familyStatementRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/cobros/familias/$guardianId',
  component: guarded(FamilyStatementScreen, 'billing:read'),
});
const setupRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/configuracion',
  component: guarded(SetupScreen, 'tenant:read'),
});
const importsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/importar',
  component: guarded(ImportsScreen, 'imports:run'),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  mfaRoute,
  forgotPasswordRoute,
  invitationRoute,
  playgroundRoute,
  actAsRoute,
  appRoute.addChildren([
    homeRoute,
    accountRoute,
    sedesRoute,
    consentsRoute,
    yearsRoute,
    yearRoute,
    levelsRoute,
    groupsRoute,
    subjectsRoute,
    classroomsRoute,
    studentsRoute,
    studentRoute,
    reportCardRoute,
    teachersRoute,
    teacherRoute,
    guardiansRoute,
    usersRoute,
    rolesRoute,
    timetableRoute,
    attendanceRoute,
    gradesViewRoute,
    reportCardsRoute,
    announcementsRoute,
    mentoriaRoute,
    albertRoute,
    curriculumRoute,
    billingRoute,
    familyStatementRoute,
    setupRoute,
    importsRoute,
  ]),
]);

/**
 * Builds the router with the stores it needs. Code-based routes: file-based routing would force
 * `__root.tsx` and `$param.tsx` names that break the kebab-case rule of dev-style.
 *
 * @param {RouterContext} context -- Auth store, API client and query client.
 * @returns The router for `RouterProvider`.
 */
export const createAppRouter = (context: RouterContext) =>
  createRouter({
    routeTree,
    context,
    defaultPreload: 'intent',
    scrollRestoration: true,
    defaultPendingMs: 200,
  });
